import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { INTERACTIVE_FOCUS } from "../lib/chrome";
import { EASE_CALM } from "../lib/motion";
import {
	CHOICE_CARD_DESKTOP_SIZE_CLASS,
	CHOICE_CARD_STACKED_SIZE_CLASS,
	ICON_GLYPH_CLASS,
	ICON_TEXT_BOTTOM_CLASS,
	ICON_TEXT_TILE_CLASS,
	ICON_TEXT_TOP_CLASS,
	QUESTION_LABEL_TYPE_CLASS,
} from "./choice-card-layout";
import { PICKER_HANDOFF_LAYOUT_MODE } from "./picker-stack-motion";
import type { TQuestionnairePath } from "./questionnaire-flow";
import { preventQuestionnairePointerScroll } from "./questionnaire-scroll";

export type TChoiceCardData = {
	path: TQuestionnairePath;
	icon: string;
	desktopLabel: string;
	mobileLabel: string;
};

/** Opacity + y enter on the card root so icon and label fade as one surface. */
export type TChoiceCardEnter = {
	opacity: number;
	y: number;
	durationS: number;
	delayS: number;
};

const CHOICE_CARD_SURFACE_CLASS = cn(
	"group/choice w-full appearance-none border-0 bg-white text-center text-teal-500 shadow-[0_10px_30px_color-mix(in_srgb,var(--color-black)_4%,transparent)] cursor-pointer no-underline",
	"hover:bg-teal-500 hover:text-white",
	"max-lg:hover:bg-white max-lg:hover:text-teal-500 max-lg:active:bg-teal-500 max-lg:active:text-white",
	INTERACTIVE_FOCUS,
);

/** Opening picker cards are 16:10. Transcript cards keep the suggestion-tile grid. */
export const CHOICE_CARD_INTRO_CLASS = cn(
	CHOICE_CARD_SURFACE_CLASS,
	QUESTION_LABEL_TYPE_CLASS,
	"flex aspect-[16/10] h-auto flex-col",
);
export const CHOICE_CARD_CLASS = cn(
	CHOICE_CARD_SURFACE_CLASS,
	"h-full",
	ICON_TEXT_TILE_CLASS,
	CHOICE_CARD_DESKTOP_SIZE_CLASS,
	CHOICE_CARD_STACKED_SIZE_CLASS,
);

type TChoiceCardProps = {
	choice: TChoiceCardData;
	className?: string;
	enter?: TChoiceCardEnter;
	layoutId?: string;
	selected?: boolean;
	/** Opening picker uses the icon well. The transcript slot matches suggestion tiles. */
	compact?: boolean;
} & ({ href: string; onSelect?: never } | { href?: never; onSelect: (path: TQuestionnairePath) => void });

const INTRO_WELL_CLASS =
	"flex size-12 shrink-0 items-center justify-center bg-greige-400 transition-colors duration-400 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover/choice:bg-purple-500 max-lg:size-[34px] max-lg:group-hover/choice:bg-greige-400 max-lg:group-active/choice:bg-purple-500";
const INTRO_TOP_CLASS = "flex flex-1 items-end justify-center px-6 pb-2";
const INTRO_BOTTOM_CLASS = "flex min-w-0 flex-1 items-start justify-center px-6 pt-2 text-center";

/**
 * One questionnaire entry card. Renders as a link when `href` is given, or as a
 * button when `onSelect` is given (picker on this page).
 */
export function ChoiceCard({
	choice,
	className,
	href,
	onSelect,
	enter,
	layoutId,
	selected = false,
	compact = false,
}: TChoiceCardProps) {
	const label = (
		<>
			<span className="max-lg:hidden">{choice.desktopLabel}</span>
			<span className="hidden max-lg:inline">{choice.mobileLabel}</span>
		</>
	);
	const content = compact ? (
		<>
			<span className={ICON_TEXT_TOP_CLASS}>
				<img
					className={cn(ICON_GLYPH_CLASS, selected && "brightness-0 invert")}
					src={choice.icon}
					alt=""
					aria-hidden="true"
				/>
			</span>
			<span className={cn(ICON_TEXT_BOTTOM_CLASS, "min-w-0")}>{label}</span>
		</>
	) : (
		<>
			<span className={INTRO_TOP_CLASS}>
				<span
					className={cn(
						INTRO_WELL_CLASS,
						selected && "bg-purple-500 group-hover/choice:bg-purple-500 max-lg:group-hover/choice:bg-purple-500",
					)}
				>
					<img className="block size-6 object-contain max-lg:size-5" src={choice.icon} alt="" aria-hidden="true" />
				</span>
			</span>
			<span className={INTRO_BOTTOM_CLASS}>{label}</span>
		</>
	);

	const sharedClassName = cn(
		compact ? CHOICE_CARD_CLASS : CHOICE_CARD_INTRO_CLASS,
		selected &&
			"bg-teal-500 text-white hover:bg-teal-500 hover:text-white max-lg:hover:bg-teal-500 max-lg:hover:text-white",
		className,
	);
	const motionTransition = layoutId
		? {
				layout: { duration: 0.55, ease: EASE_CALM },
				...(enter
					? { duration: enter.durationS, delay: enter.delayS, ease: EASE_CALM }
					: { duration: 0.55, ease: EASE_CALM }),
			}
		: enter
			? {
					duration: enter.durationS,
					delay: enter.delayS,
					ease: EASE_CALM,
				}
			: undefined;
	const motionProps = enter
		? {
				initial: false as const,
				animate: { opacity: enter.opacity, y: enter.y },
				transition: motionTransition,
			}
		: layoutId
			? {
					transition: motionTransition,
				}
			: undefined;
	const layoutProps = layoutId ? { layout: PICKER_HANDOFF_LAYOUT_MODE, layoutId } : undefined;

	if (href !== undefined) {
		if (enter || layoutId) {
			return (
				<motion.a
					href={href}
					className={sharedClassName}
					aria-current={selected ? "true" : undefined}
					data-questionnaire-choice={choice.path}
					data-selected={selected ? "" : undefined}
					{...motionProps}
					{...layoutProps}
				>
					{content}
				</motion.a>
			);
		}
		return (
			<a
				href={href}
				className={sharedClassName}
				aria-current={selected ? "true" : undefined}
				data-questionnaire-choice={choice.path}
				data-selected={selected ? "" : undefined}
			>
				{content}
			</a>
		);
	}

	if (enter || layoutId) {
		return (
			<motion.button
				type="button"
				className={sharedClassName}
				aria-pressed={selected}
				data-questionnaire-choice={choice.path}
				data-selected={selected ? "" : undefined}
				onPointerDown={preventQuestionnairePointerScroll}
				onClick={() => onSelect(choice.path)}
				{...motionProps}
				{...layoutProps}
			>
				{content}
			</motion.button>
		);
	}

	return (
		<button
			type="button"
			className={sharedClassName}
			aria-pressed={selected}
			data-questionnaire-choice={choice.path}
			data-selected={selected ? "" : undefined}
			onPointerDown={preventQuestionnairePointerScroll}
			onClick={() => onSelect(choice.path)}
		>
			{content}
		</button>
	);
}
